import { ReadField } from 'components/ReadField';
import { type DismantlingCoefficientTypeEnum, RatioRecordStatusEnum, RatioTypeEnum } from 'gql/graphql';
import { cnMixSpace, Grid, GridItem, IconWarning, Informer, Loader } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import cn from 'classnames';
import type React from 'react';
import type { Control, UseControllerProps } from 'react-hook-form';

import { ControlledTextField, ControlNumberField } from '../Form';

import { ControlApplicationAreaSelector } from './ApplicationAreaSelect';
import { ControlGroupTypesUerSelector } from './GroupTypeUersSelector/ControlGroupTypesUerSelector';
import { dismantlingMaterialRule, positiveCoefficientRule } from './validation';

import Styles from './EditRatioForm.module.css';

interface IEditRatioFormProps {
    isDataFetching: boolean;
    ratioId?: number | string | null;
    control: Control;
    isCustom: boolean | null | undefined;
    selectedCoefficientType?: { id: RatioTypeEnum; label: string };
    ratioType: RatioTypeEnum;
    groupType?: DismantlingCoefficientTypeEnum | null;
    appliesToUerIdsControl: UseControllerProps;
    ratioGroupIdControl: UseControllerProps;
    groupTypeControl: UseControllerProps;
    isApplicationAreaDisabled: boolean;
    applicationSelectorDisabledIds: any[];
    applicationAreaTooltipText?: string;
    contractId: number;
    uerId: number;
    editingRatioStatus?: RatioRecordStatusEnum;
}

export const EditRatioForm: React.FC<IEditRatioFormProps> = (props) => {
    const {
        isDataFetching,
        ratioId,
        control,
        isCustom,
        selectedCoefficientType,
        ratioType,
        appliesToUerIdsControl,
        ratioGroupIdControl,
        groupTypeControl,
        isApplicationAreaDisabled,
        applicationSelectorDisabledIds,
        applicationAreaTooltipText,
        contractId,
        uerId,
        groupType,
        editingRatioStatus,
    } = props;

    return isDataFetching ? (
        <div className={Styles.createCoefficientBody}>
            <Loader />
        </div>
    ) : (
        <div className={Styles.createCoefficientBody}>
            <Grid rowGap="s" cols="1">
                {!ratioId && (
                    <GridItem className={Styles.wrapInformer}>
                        <Informer status="warning" size="s" icon={IconWarning}>
                            Новый коэффициент будет добавлен в Прейскурант коэффициентов.
                        </Informer>
                    </GridItem>
                )}
                <GridItem>
                    <ReadField
                        value={selectedCoefficientType?.label}
                        label="Тип коэффициента"
                        classWrapper={Styles.wrapper}
                        classFirstText={cn(Styles.leftBlock, cnMixSpace({ mR: 's' }))}
                    />
                </GridItem>
                <GridItem>
                    <ControlledTextField
                        name="code"
                        label="Шифр"
                        control={control}
                        type="text"
                        placeholder="Новый"
                        disabled
                    />
                </GridItem>
                <GridItem>
                    <ControlledTextField
                        name="workConditions"
                        label="Наименование"
                        control={control}
                        type="textarea"
                        rules={requiredBaseRule}
                        disabled={!!ratioId && !isCustom}
                    />
                </GridItem>
                <GridItem>
                    <ControlNumberField
                        name="kozp"
                        label="К озп"
                        labelPosition="left"
                        control={control}
                        size="s"
                        rules={positiveCoefficientRule}
                    />
                </GridItem>
                <GridItem>
                    <ControlNumberField
                        name="kem"
                        label="К эмм"
                        labelPosition="left"
                        control={control}
                        size="s"
                        rules={positiveCoefficientRule}
                    />
                </GridItem>
                <GridItem>
                    <ControlNumberField
                        name="ktz"
                        label="К тз"
                        labelPosition="left"
                        control={control}
                        size="s"
                        rules={positiveCoefficientRule}
                    />
                </GridItem>
                <GridItem>
                    <ControlNumberField
                        name="ktzzpm"
                        label="Ктз зпм"
                        labelPosition="left"
                        control={control}
                        size="s"
                        rules={positiveCoefficientRule}
                    />
                </GridItem>
                <GridItem>
                    <ControlNumberField
                        name="kmat"
                        label="К мат"
                        labelPosition="left"
                        control={control}
                        size="s"
                        rules={
                            ratioType === RatioTypeEnum.DismantlingCoef
                                ? dismantlingMaterialRule
                                : positiveCoefficientRule
                        }
                    />
                </GridItem>
                <GridItem>
                    <ControlNumberField
                        name="knr"
                        label="Кнр"
                        labelPosition="left"
                        control={control}
                        size="s"
                        rules={positiveCoefficientRule}
                    />
                </GridItem>
                <GridItem>
                    <ControlNumberField
                        name="ksp"
                        label="Ксп"
                        labelPosition="left"
                        control={control}
                        size="s"
                        rules={positiveCoefficientRule}
                    />
                </GridItem>
                <GridItem>
                    {ratioType === RatioTypeEnum.DismantlingCoef ? (
                        <ControlGroupTypesUerSelector
                            label="Области применения"
                            groupTypeControl={groupTypeControl}
                            appliesToUerIdsControl={appliesToUerIdsControl}
                            contractId={contractId}
                            isSelectDisabled={
                                (!!ratioId && editingRatioStatus !== RatioRecordStatusEnum.Draft) || !!groupType
                            }
                            uerId={uerId}
                        />
                    ) : (
                        <ControlApplicationAreaSelector
                            label="Области применения"
                            coefficientType={ratioType}
                            appliesToUerIdsControl={appliesToUerIdsControl}
                            ratioGroupIdControl={ratioGroupIdControl}
                            disabled={isApplicationAreaDisabled}
                            disabledIds={applicationSelectorDisabledIds}
                            tooltipText={applicationAreaTooltipText}
                        />
                    )}
                </GridItem>
                <GridItem>
                    <ControlledTextField
                        name="justification"
                        label="Обоснование"
                        control={control}
                        type="textarea"
                        rules={requiredBaseRule}
                    />
                </GridItem>
            </Grid>
        </div>
    );
};

EditRatioForm.displayName = 'EditRatioForm';
